import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import BackgroundFX from '../components/backgroundFX'
import BuyCreditsModal from '../components/buyCreditsModal'
import { DEFAULT_QUESTION_COUNTS, DIFFICULTIES, QUESTION_TYPES } from '../constants/config'
import { STRINGS } from '../constants/strings'
import { api } from '../data/api'
import { useAuth, useExam } from '../hooks'

function useTypewriter(lines, speed = 45) {
  const [lineIdx,  setLineIdx]  = useState(0)
  const [charIdx,  setCharIdx]  = useState(0)
  const [done,     setDone]     = useState(false)
  const timerRef = useRef(null)

  useEffect(() => {
    if (done) return
    const current = lines[lineIdx]
    if (charIdx < current.length) {
      timerRef.current = setTimeout(() => setCharIdx(c => c + 1), speed)
    } else if (lineIdx < lines.length - 1) {
      timerRef.current = setTimeout(() => { setLineIdx(l => l + 1); setCharIdx(0) }, 250)
    } else {
      setDone(true)
    }
    return () => clearTimeout(timerRef.current)
  }, [charIdx, lineIdx, done])

  return { lines, lineIdx, charIdx, done }
}

const Q_TYPES = QUESTION_TYPES

function ExamDropdown({ exams, selected, onChange }) {
  const [open,   setOpen]   = useState(false)
  const [search, setSearch] = useState('')
  const ref      = useRef(null)
  const inputRef = useRef(null)

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setSearch('') }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const filtered  = exams.filter(e => e.name.toLowerCase().includes(search.toLowerCase()))
  const placeholder = selected ? selected.name : 'Select an exam…'

  return (
    <div ref={ref} className="relative flex-1">
      <div className="relative flex items-center">
        <input ref={inputRef} type="text" value={search}
          onChange={e => { setSearch(e.target.value); setOpen(true) }}
          onFocus={() => { setOpen(true); setSearch('') }}
          onBlur={() => setTimeout(() => { if (!ref.current?.contains(document.activeElement)) { setOpen(false); setSearch('') } }, 100)}
          placeholder={placeholder}
          className="w-full px-3 py-1.5 pr-7 rounded-lg text-xs font-medium outline-none transition-all truncate"
          style={{ background: 'var(--chip-bg)', border: `1px solid ${open ? 'var(--brand)' : 'var(--chip-border)'}`, color: 'var(--text-1)' }}
        />
        <svg className={`absolute right-2 w-3 h-3 pointer-events-none transition-transform ${open ? 'rotate-180' : ''}`}
             style={{ color: 'var(--text-3)' }} viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
          <path d="M2 4l4 4 4-4" />
        </svg>
      </div>
      {open && (
        <div className="absolute left-0 right-0 top-full mt-1 rounded-xl overflow-hidden shadow-2xl z-50"
             style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
          <div className="max-h-48 overflow-y-auto">
            {filtered.length === 0
              ? <p className="px-3 py-3 text-xs text-center" style={{ color: 'var(--text-3)' }}>No exams found</p>
              : filtered.map(exam => {
                  const sel = selected?.id === exam.id
                  return (
                    <button key={exam.id} type="button"
                      onMouseDown={e => { e.preventDefault(); onChange(exam); setOpen(false); setSearch('') }}
                      className="w-full text-left px-3 py-2 transition-colors"
                      style={{ background: sel ? 'var(--sel-bg)' : 'transparent', borderBottom: '1px solid var(--border)' }}
                      onMouseOver={e => { if (!sel) e.currentTarget.style.background = 'var(--surface-2)' }}
                      onMouseOut={e  => { if (!sel) e.currentTarget.style.background = sel ? 'var(--sel-bg)' : 'transparent' }}
                    >
                      <p className="text-xs font-semibold truncate" style={{ color: sel ? 'var(--sel-text)' : 'var(--text-1)' }}>{exam.name}</p>
                    </button>
                  )
                })
            }
          </div>
        </div>
      )}
    </div>
  )
}

// Remembers exam codes this browser has successfully looked up, so retrying
// the same exam (the common case — Trials, then the real Exam, then maybe
// again) never needs the code copy-pasted in by hand a second time.
const RECENT_CODES_KEY = 'skillassess_recent_exam_codes'
const MAX_RECENT_CODES = 8

function loadRecentCodes() {
  try {
    const raw = localStorage.getItem(RECENT_CODES_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function saveRecentCode(code, name) {
  try {
    const existing = loadRecentCodes().filter(r => r.code !== code)
    const next = [{ code, name }, ...existing].slice(0, MAX_RECENT_CODES)
    localStorage.setItem(RECENT_CODES_KEY, JSON.stringify(next))
    return next
  } catch {
    return loadRecentCodes()
  }
}

function ExamCodeEntry({ onFound, skipAutoFind = false }) {
  const initialRecent = loadRecentCodes()
  // A single remembered code pre-fills the field immediately, so there's no
  // blank state while the auto-lookup effect below runs on mount — unless the
  // examinee just explicitly cleared it, in which case it starts empty, not
  // re-filled with the same code they just dismissed.
  const [code,    setCode]    = useState(() => (!skipAutoFind && initialRecent.length === 1) ? initialRecent[0].code : '')
  const [loading, setLoading] = useState(false)
  const [error,   setError]   = useState('')
  const [recent,  setRecent]  = useState(initialRecent)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const wrapRef       = useRef(null)
  const autoTriedRef  = useRef(false)

  useEffect(() => {
    function onClickOutside(e) { if (wrapRef.current && !wrapRef.current.contains(e.target)) setDropdownOpen(false) }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [])

  // Only a code that actually resolved gets remembered — saving a code that
  // came back "invalid" would just clutter the dropdown with codes that
  // don't work, so a failed attempt here never shows up in it.
  async function findCode(value) {
    if (!value.trim()) return
    setError(''); setLoading(true)
    try {
      const exam = await api.get(`/api/exams/code/${value.trim()}`)
      setRecent(saveRecentCode(exam.examCode ?? value.trim(), exam.name))
      onFound({ ...exam, id: exam._id })
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  // One remembered code — look it up right away, no click required. Skipped
  // when the examinee just dismissed this exact auto-selection, so clearing
  // it doesn't instantly bring it right back.
  useEffect(() => {
    if (autoTriedRef.current || skipAutoFind) return
    if (initialRecent.length === 1) {
      autoTriedRef.current = true
      findCode(initialRecent[0].code)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function handleFind(e) {
    e.preventDefault()
    setDropdownOpen(false)
    findCode(code)
  }

  function handlePickRecent(r) {
    setDropdownOpen(false)
    setCode(r.code)
    findCode(r.code)
  }

  return (
    <div ref={wrapRef} className="flex-1 flex flex-col gap-1 relative">
      <form onSubmit={handleFind} className="flex gap-1.5">
        <div className="relative flex-1 min-w-0">
          <input
            type="text"
            value={code}
            onChange={e => { setCode(e.target.value.toUpperCase()); setError('') }}
            // Clicking/focusing always offers whatever's saved — even just
            // one code — so dismissing an auto-filled code doesn't lose it;
            // it's still one click away instead of needing to be retyped.
            onFocus={e => { if (!error) e.currentTarget.style.borderColor = 'var(--brand)'; if (recent.length > 0) setDropdownOpen(true) }}
            onBlur={e => { e.currentTarget.style.borderColor = error ? 'var(--red)' : 'var(--chip-border)' }}
            placeholder={recent.length ? 'Enter or pick a saved exam code…' : 'Enter exam code…'}
            maxLength={10}
            className="w-full pl-3 pr-7 py-1.5 rounded-lg text-xs font-mono font-bold tracking-widest outline-none transition-all"
            style={{
              background: 'var(--chip-bg)',
              border:     `1px solid ${error ? 'var(--red)' : 'var(--chip-border)'}`,
              color:      'var(--text-1)',
            }}
          />
          {code && !loading && (
            <button type="button" onClick={() => { setCode(''); setError('') }} title="Clear"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-xs"
              style={{ color: 'var(--text-3)' }}>✕</button>
          )}

          {dropdownOpen && recent.length > 0 && (
            <div className="absolute left-0 right-0 top-full mt-1 rounded-xl overflow-hidden shadow-2xl z-50"
                 style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
              <div className="max-h-48 overflow-y-auto">
                {recent.map(r => (
                  <button key={r.code} type="button" onMouseDown={e => e.preventDefault()} onClick={() => handlePickRecent(r)}
                    className="w-full text-left px-3 py-2 transition-colors"
                    style={{ borderBottom: '1px solid var(--border)' }}
                    onMouseOver={e => e.currentTarget.style.background = 'var(--surface-2)'}
                    onMouseOut={e  => e.currentTarget.style.background = 'transparent'}>
                    <p className="text-xs font-mono font-bold tracking-widest" style={{ color: 'var(--text-1)' }}>{r.code}</p>
                    {r.name && <p className="text-[0.65rem] truncate" style={{ color: 'var(--text-3)' }}>{r.name}</p>}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <button
          type="submit"
          disabled={loading || !code.trim()}
          className="flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-bold transition-all disabled:opacity-40"
          style={{ background: 'var(--brand)', color: '#fff' }}
        >
          {loading ? '…' : 'Find'}
        </button>
      </form>
      {error && <p className="text-[0.65rem] leading-tight" style={{ color: 'var(--red)' }}>{error}</p>}
    </div>
  )
}

function scaledMin(total) { return Math.max(5, total) }

function Stepper({ value, onChange, min = 0, max = 100, disabled = false }) {
  function handleType(e) {
    if (disabled) return
    const raw = e.target.value
    if (raw === '' || raw === '-') { onChange(min); return }
    const n = parseInt(raw, 10)
    if (isNaN(n)) return
    onChange(Math.min(max, Math.max(min, n)))
  }

  return (
    <div className="flex items-center gap-1.5">
      <button
        onClick={() => onChange(Math.max(min, value - 1))}
        disabled={value <= min || disabled}
        className="w-7 h-7 rounded-lg flex items-center justify-center text-sm font-bold transition-all disabled:opacity-30 disabled:cursor-not-allowed"
        style={{ background: 'var(--chip-bg)', border: '1px solid var(--chip-border)', color: 'var(--text-2)', visibility: disabled ? 'hidden' : 'visible' }}
      >−</button>

      <input
        type="number"
        value={value}
        min={min}
        max={max}
        readOnly={disabled}
        onChange={handleType}
        className="w-12 text-center font-bold text-sm rounded-lg py-1 outline-none transition-shadow
                   [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
        style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-1)', cursor: disabled ? 'default' : 'text' }}
        onFocus={e  => { if (!disabled) { e.currentTarget.style.boxShadow = '0 0 0 2px var(--brand)'; e.currentTarget.select() } }}
        onBlur={e   => { e.currentTarget.style.boxShadow = 'none' }}
      />

      <button
        onClick={() => onChange(Math.min(max, value + 1))}
        disabled={value >= max || disabled}
        className="w-7 h-7 rounded-lg flex items-center justify-center text-sm font-bold transition-all disabled:opacity-30 disabled:cursor-not-allowed"
        style={{ background: 'var(--chip-bg)', border: '1px solid var(--chip-border)', color: 'var(--text-2)', visibility: disabled ? 'hidden' : 'visible' }}
      >+</button>
    </div>
  )
}

function Chip({ children }) {
  return (
    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs"
         style={{ background: 'var(--chip-bg)', border: '1px solid var(--chip-border)' }}>
      {children}
    </div>
  )
}

// Single source of truth for the exam rules — shown both in the compact inline
// box on the setup screen and in the full-screen reader opened from it.
const EXAM_TERMS = [
  { icon: '⏱', text: 'Timer starts immediately and cannot be paused.' },
  { icon: '🚫', text: 'Navigating away will not stop the timer.' },
  { icon: '✅', text: 'Answers are final once submitted or time runs out.' },
  { icon: '🔕', text: 'No hints or feedback shown during the exam.' },
  { icon: '📊', text: 'Results generate your personalised study report.' },
  { icon: '⚖️', text: 'Unauthorised assistance violates exam integrity.' },
  { icon: '🖥️', text: 'Exam runs in fullscreen. Exiting fullscreen will show a warning.' },
  { icon: '🔒', text: 'Switching tabs or windows will automatically submit your exam.' },
  { icon: '📋', text: 'Copy, cut, and paste are disabled during the exam.' },
  { icon: '📡', text: 'Screen sharing via browser will automatically submit your exam.' },
  { icon: '🔀', text: 'Questions and options are shuffled for each attempt.' },
  { icon: '👤', text: 'A dynamic watermark with your name and email is visible throughout the exam.' },
  { icon: '📱', text: 'Only one active session is allowed. Logging in on another device will submit your exam.' },
]

// Grading specifics per question type — only shown for types actually enabled
// on the selected exam, so a student knows exactly how each type is scored.
const QUESTION_TYPE_NOTES = {
  mcq:          { icon: '📋', text: 'Multiple Choice — select the single best answer.' },
  true_false:   { icon: '✅', text: 'True / False — choose the correct option.' },
  short_answer: { icon: '📝', text: 'Descriptive — write your answer in up to 500 characters. Marks are awarded based on how many key points you cover: full marks for all, partial for some, zero if none.' },
  msq:          { icon: '☑️', text: 'Multiple Select (MSQ) — select all options that apply. Picking even one incorrect option scores 0 for that question; partial credit is only given when every option you selected is correct.' },
}

function TermsFullScreenModal({ questionTypeNotes, onClose }) {
  return createPortal(
    <div className="fixed inset-0 z-[9999] flex flex-col" style={{ background: 'var(--bg)' }}>
      <div className="flex items-center justify-between px-4 sm:px-8 py-4 border-b flex-shrink-0" style={{ borderColor: 'var(--border)' }}>
        <div>
          <h2 className="text-base sm:text-lg font-bold" style={{ color: 'var(--text-1)' }}>Exam Terms & Conditions</h2>
        </div>
        <button onClick={onClose}
          className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-all"
          style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-2)' }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--brand)'; e.currentTarget.style.color = 'var(--brand)' }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-2)' }}
        >
          <svg className="w-5 h-5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 4L4 12M4 4l8 8"/>
          </svg>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 sm:px-8 py-6">
        <div className="max-w-2xl mx-auto space-y-6">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--text-3)' }}>Exam Rules</p>
            <div className="space-y-3">
              {EXAM_TERMS.map(({ icon, text }, i) => (
                <div key={i} className="flex items-start gap-3">
                  <span className="text-sm flex-shrink-0 mt-px">{icon}</span>
                  <span className="text-sm leading-relaxed" style={{ color: 'var(--text-2)' }}>{text}</span>
                </div>
              ))}
            </div>
          </div>

          {questionTypeNotes.length > 0 && (
            <div>
              <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--text-3)' }}>How Each Question Type Is Graded</p>
              <div className="space-y-3">
                {questionTypeNotes.map(({ icon, text }, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span className="text-sm flex-shrink-0 mt-px">{icon}</span>
                    <span className="text-sm leading-relaxed" style={{ color: 'var(--text-2)' }}>{text}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}

export default function SetupScreen() {
  const { user, updateUser } = useAuth()
  const { state, dispatch } = useExam()
  const [starting,          setStarting]          = useState(false)
  const [termsAccepted,     setTermsAccepted]     = useState(false)
  // Once the examinee explicitly clears an exam that was auto-selected from a
  // single remembered code, stop re-running that auto-lookup — otherwise
  // clearing it just reselects the same exam a moment later.
  const [skipAutoFind,      setSkipAutoFind]      = useState(false)
  const [termsFullScreen,   setTermsFullScreen]   = useState(false)
  const [exams,             setExams]             = useState([])
  const [examsLoading,      setExamsLoading]      = useState(true)
  const [activeSessionError,setActiveSessionError] = useState('')
  const [buyCreditsOpen,    setBuyCreditsOpen]    = useState(false)
  const tw = useTypewriter([STRINGS.hero.headline1, STRINGS.hero.headline2])

  // 'org'        → org admin, sees their own exams in dropdown
  // 'standalone' → user with no orgId, sees superadmin exams in dropdown
  // 'code'       → user linked to an org, must enter exam code
  const examMode = user?.role === 'organisation' ? 'org'
                 : user?.orgId                   ? 'code'
                 :                                 'standalone'

  useEffect(() => {
    if (examMode === 'standalone') dispatch({ type: 'SET_MODE', payload: 'practice' })
    if (!state.readiness) api.get('/api/users/readiness').then(r => dispatch({ type: 'SET_READINESS', payload: r })).catch(() => {})
    if (examMode === 'org') {
      api.get('/api/exams?limit=200')
        .then(data => {
          const list = data.exams ?? data
          // Only exams with fully generated questions are pickable here — nothing
          // to preview/take on one that's still generating or failed.
          const normalized = list.map(e => ({ ...e, id: e._id?.toString() ?? e.id })).filter(e => e.questionStatus === 'ready')
          setExams(normalized)
          setExamsLoading(false)
          if (normalized.length === 1) dispatch({ type: 'SET_EXAM', payload: normalized[0] })
        })
        .catch(() => setExamsLoading(false))
    } else if (examMode === 'standalone') {
      api.get('/api/exams/available')
        .then(list => {
          const normalized = list.map(e => ({ ...e, id: e._id?.toString() ?? e.id })).filter(e => e.questionStatus === 'ready')
          setExams(normalized)
          setExamsLoading(false)
          if (normalized.length === 1) dispatch({ type: 'SET_EXAM', payload: normalized[0] })
        })
        .catch(() => setExamsLoading(false))
    } else {
      setExamsLoading(false)
    }
  }, [])

  const [availableCounts, setAvailableCounts] = useState(null)
  // Only surfaced after an actual Start attempt with zero questions picked —
  // not just because the breakdown happens to default to 0 before anything's
  // been chosen yet.
  const [triedStartEmpty, setTriedStartEmpty] = useState(false)

  const isVirtual = state.mode === 'virtual'
  const examQS    = state.selectedExam?.questionSettings
  const examId    = state.selectedExam?._id ?? state.selectedExam?.id
  const questionTypeNotes = examQS
    ? Object.entries(examQS).filter(([type, s]) => s?.enabled && QUESTION_TYPE_NOTES[type]).map(([type]) => QUESTION_TYPE_NOTES[type])
    : []

  // Trial question counts default to the selected exam's own configured counts,
  // not the generic platform default — falls back to that default when no exam
  // is selected (or after deselecting one).
  useEffect(() => {
    if (isVirtual) return
    if (!examQS) {
      dispatch({ type: 'SET_QUESTION_COUNTS', payload: { ...DEFAULT_QUESTION_COUNTS } })
      return
    }
    const counts = {}
    for (const type of ['mcq', 'true_false', 'short_answer', 'msq']) {
      counts[type] = examQS[type]?.enabled ? (examQS[type].count ?? 0) : 0
    }
    dispatch({ type: 'SET_QUESTION_COUNTS', payload: counts })
  }, [examId, isVirtual])

  // The org that owns an exam is previewing/testing it from their own dashboard
  // ('org' mode) — level-locking is a restriction meant for real students (the
  // 'code' and 'standalone' flows), not the examiner checking their own content.
  // Trials on/off still applies to everyone, including the org: if it's disabled,
  // no practice pools were ever generated, so there's nothing to preview either way.
  const isStudentFlow = examMode !== 'org'

  // Force Virtual mode if the selected exam has Trials disabled but Practice is
  // still active (e.g. left over from a previously selected exam). Standalone
  // users have no Virtual mode at all, so this is skipped for them — see the
  // known limitation in the Trials feature plan.
  useEffect(() => {
    if (examMode !== 'standalone' && state.selectedExam?.trialsEnabled === false && state.mode === 'practice') {
      dispatch({ type: 'SET_MODE', payload: 'virtual' })
    }
  }, [state.selectedExam, state.mode])

  const TRIAL_LEVEL_ORDER = ['Beginner', 'Intermediate', 'Advanced', 'Expert']
  const [trialProgress, setTrialProgress] = useState(null)

  // Fetched whenever Trials are in play at all (not just when level-locking is
  // on) — isLevelLocked/examLockedByTrials below still only act on it when
  // trialLevelLockEnabled is actually set; the broader fetch is so the attempt
  // counts shown per level stay accurate even when there's no lock.
  useEffect(() => {
    if (!isStudentFlow || !examId || state.selectedExam?.trialsEnabled === false) { setTrialProgress(null); return }
    api.get(`/api/users/trial-progress/${examId}`)
      .then(setTrialProgress)
      .catch(() => setTrialProgress(null))
  }, [examId, state.selectedExam?.trialsEnabled])

  function isLevelLocked(levelKey) {
    if (!isStudentFlow || !state.selectedExam?.trialLevelLockEnabled || !trialProgress) return false
    const idx = TRIAL_LEVEL_ORDER.indexOf(levelKey)
    if (idx <= 0) return false
    return !trialProgress[TRIAL_LEVEL_ORDER[idx - 1]]?.passed
  }

  // With level-locking on, the actual exam itself stays locked until every Trial
  // level is cleared — students only, org admins previewing are exempt.
  const examLockedByTrials = isStudentFlow && state.selectedExam?.trialLevelLockEnabled
    && !(trialProgress && TRIAL_LEVEL_ORDER.every(l => trialProgress[l]?.passed))

  useEffect(() => {
    if (examLockedByTrials && state.mode === 'virtual') {
      dispatch({ type: 'SET_MODE', payload: 'practice' })
    }
  }, [examLockedByTrials, state.mode])

  useEffect(() => {
    if (!examId || isVirtual) { setAvailableCounts(null); return }
    const difficulty = (state.difficulty ?? 'Intermediate').toLowerCase()
    api.get(`/api/exams/${examId}/question-counts?difficulty=${difficulty}`)
      .then(data => {
        setAvailableCounts(data)
        dispatch({ type: 'CLAMP_QUESTION_COUNTS', payload: data })
      })
      .catch(() => setAvailableCounts(null))
  }, [examId, state.difficulty, isVirtual])

  // With level-locking on, a Trial's question count per type can never go below
  // what the admin configured for the real exam — a student can still ask for
  // more (that only makes the attempt harder, never easier), but can't shrink it
  // to game an easy pass. Enforced server-side in getExamQuestions; this keeps
  // the UI's min in sync with that floor.
  const countsFloorByTrialLock = isStudentFlow && !isVirtual && state.selectedExam?.trialLevelLockEnabled
  const trialCountFloor = key => countsFloorByTrialLock && examQS?.[key]?.enabled ? (examQS[key].count ?? 0) : 0

  // In virtual mode, counts and difficulty are locked to what the admin configured
  const qc = isVirtual && examQS
    ? Object.fromEntries(Object.entries(examQS).filter(([, s]) => s.enabled).map(([k, s]) => [k, s.count]))
    : state.questionCounts
  const total = (qc.mcq ?? 0) + (qc.true_false ?? 0) + (qc.short_answer ?? 0) + (qc.msq ?? 0)

  // Bump any count that's below the trial-lock floor back up — mirrors the
  // existing "clamp down to availableCounts" effect below, just in the other direction.
  useEffect(() => {
    if (!countsFloorByTrialLock) return
    for (const key of ['mcq', 'true_false', 'short_answer', 'msq']) {
      const floor = trialCountFloor(key)
      if (floor > (state.questionCounts[key] ?? 0)) {
        dispatch({ type: 'SET_QUESTION_COUNTS', payload: { [key]: floor } })
      }
    }
  }, [countsFloorByTrialLock, examQS])

  const activeDifficulty = isVirtual
    ? (state.selectedExam?.difficulty ?? 'Intermediate')
    : state.difficulty

  function setCount(key, val) {
    dispatch({ type: 'SET_QUESTION_COUNTS', payload: { [key]: val } })
  }

  async function doStart() {
    setStarting(true)
    setActiveSessionError('')
    try {
      if (isVirtual) {
        const examId = state.selectedExam._id ?? state.selectedExam.id
        const data = await api.post('/api/users/attempts/start', { examId })
        const durationSec = scaledMin(total) * 60
        dispatch({ type: 'START_EXAM', payload: { questions: data.questions, mode: 'virtual', durationSec, attemptId: data.attempt._id } })
      } else {
        const examId = state.selectedExam._id ?? state.selectedExam.id
        const data   = await api.post(`/api/exams/${examId}/questions`, { questionCounts: qc, difficulty: state.difficulty })
        dispatch({ type: 'START_EXAM', payload: { questions: data.questions, mode: 'practice', durationSec: null } })
      }
    } catch (err) {
      if (err.status === 409) {
        setActiveSessionError(err.message)
      } else if (err.code === 'INSUFFICIENT_CREDITS' && examMode !== 'code') {
        // Standalone users and org admins (previewing their own exam) both pay
        // for their own usage — unlike an org-linked examinee ('code'), who has
        // no balance of their own and can only be told to contact their
        // organisation — so they can act on this immediately.
        setBuyCreditsOpen(true)
      } else {
        setActiveSessionError(err.message || 'Failed to start exam. Please try again.')
      }
    } finally { setStarting(false) }
  }

  const rd = state.readiness

  return (
    <>

      {/* ── Full-width hero ─────────────────────────────────────────────── */}
      <section className="relative overflow-hidden flex-1"
               style={{ background: 'var(--hero-bg)', borderBottom: '1px solid var(--config-border)' }}>

        <div className="absolute inset-0 pointer-events-none"
             style={{ opacity: 'var(--hero-grid-op)', backgroundImage: `linear-gradient(var(--hero-grid) 1px,transparent 1px),linear-gradient(90deg,var(--hero-grid) 1px,transparent 1px)`, backgroundSize: '48px 48px' }} />
        <BackgroundFX />

        {/* 2-column grid */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 py-10 lg:py-16 grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">

          {/* ── LEFT: Hero text ──────────────────────────────────────── */}
          <div>
            {/* Shimmer badge */}
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold mb-5"
                 style={{
                   background: 'var(--badge-bg)',
                   border: '1px solid var(--badge-border)',
                   animation: 'fade-up 0.4s ease both',
                 }}>
              <span className="w-1.5 h-1.5 rounded-full bg-sky-400 animate-pulse" />
              <span style={{
                background: 'linear-gradient(90deg, var(--badge-text) 0%, #fff 40%, var(--badge-text) 80%)',
                backgroundSize: '200% auto',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                animation: 'shimmer-slide 3s linear infinite',
              }}>{STRINGS.hero.badge}</span>
            </div>

            {/* Typewriter headline */}
            <h1 className="font-black tracking-tight leading-[1.05] mb-4"
                style={{ animation: 'fade-up 0.4s 0.1s ease both', opacity: 0 }}>
              <span className="block text-4xl sm:text-5xl xl:text-7xl"
                    style={{ background: 'linear-gradient(135deg,var(--text-1) 0%,var(--text-2) 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                {tw.lineIdx > 0 || tw.done ? STRINGS.hero.headline1 : STRINGS.hero.headline1.slice(0, tw.charIdx)}
                {tw.lineIdx === 0 && !tw.done && (
                  <span style={{ WebkitTextFillColor: '#94a3b8' }} className="animate-pulse">|</span>
                )}
              </span>
              <span className="block text-4xl sm:text-5xl xl:text-7xl mt-1"
                    style={{ background: 'linear-gradient(135deg,#38bdf8 0%,#818cf8 50%,#c084fc 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                {tw.lineIdx >= 1 ? STRINGS.hero.headline2.slice(0, tw.lineIdx > 1 || tw.done ? undefined : tw.charIdx) : ''}
                {tw.lineIdx === 1 && !tw.done && (
                  <span style={{ WebkitTextFillColor: '#818cf8' }} className="animate-pulse">|</span>
                )}
              </span>
            </h1>

            <p className="text-base leading-relaxed mb-5 max-w-md"
               style={{ color: 'var(--text-2)', animation: 'fade-up 0.4s 0.25s ease both', opacity: 0 }}>
              {examMode === 'org' ? STRINGS.hero.descriptionAdmin
                : examMode === 'code' ? STRINGS.hero.descriptionUser
                : STRINGS.hero.descriptionStandalone}
            </p>

            {rd && rd.total_attempts > 0 ? (
              <div className="flex flex-wrap gap-3 mb-5"
                   style={{ animation: 'fade-up 0.4s 0.35s ease both', opacity: 0 }}>
                {[
                  { v: rd.total_attempts,      l: 'Attempts',  c: 'var(--brand)' },
                  { v: `${rd.pass_rate}%`,      l: 'Pass rate', c: 'var(--amber)' },
                  { v: `${rd.avg_percentage}%`, l: 'Avg score', c: 'var(--green)' },
                  { v: rd.trend === 'improving' ? '📈 Improving' : '📉 Declining',
                    c: rd.trend === 'improving' ? 'var(--green)' : 'var(--red)' },
                ].map(({ v, l, c }) => (
                  <Chip key={v}>
                    <span className="font-bold" style={{ color: c }}>{v}</span>
                    {l && <span style={{ color: 'var(--text-3)' }}>{l}</span>}
                  </Chip>
                ))}
              </div>
            ) : (
              <div className="flex flex-wrap gap-3 mb-5"
                   style={{ animation: 'fade-up 0.4s 0.35s ease both', opacity: 0 }}>
                {(examMode === 'code' ? STRINGS.hero.featuresUser : STRINGS.hero.features).map(({ icon, text }) => (
                  <Chip key={text}>
                    <span>{icon}</span>
                    <span style={{ color: 'var(--text-2)' }}>{text}</span>
                  </Chip>
                ))}
              </div>
            )}

            {examMode === 'org' && (
              <p className="text-xs flex items-center gap-1.5"
                 style={{ color: 'var(--text-3)', animation: 'fade-up 0.4s 0.45s ease both', opacity: 0 }}>
                <span>📄</span>
                Upload your PDFs in Admin to generate questions from your own content
              </p>
            )}
          </div>

          {/* ── RIGHT: Config card ──────────────────────────────────── */}
          <div className="relative w-full sticky top-20 self-start"
               style={{ animation: 'fade-up 0.5s 0.2s ease both', opacity: 0 }}>
            <div className="rounded-2xl overflow-hidden backdrop-blur-xl"
                 style={{ background: 'var(--config-bg)', border: '1px solid var(--config-border)' }}>

              {/* Exam picker */}
              <div className="flex items-center gap-3 px-4 py-2.5 border-b" style={{ borderColor: 'var(--config-border)' }}>
                <span className="text-[0.68rem] font-bold uppercase tracking-widest flex-shrink-0" style={{ color: 'var(--text-3)' }}>Exam</span>
                {examMode !== 'code' ? (
                  examsLoading
                    ? <span className="text-xs" style={{ color: 'var(--text-3)' }}>Loading…</span>
                    : exams.length === 0
                      ? <span className="text-xs" style={{ color: 'var(--text-3)' }}>No exams available.</span>
                      : <ExamDropdown exams={exams} selected={state.selectedExam}
                          onChange={exam => dispatch({ type: 'SET_EXAM', payload: exam })} />
                ) : (
                  state.selectedExam ? (
                    <div className="flex flex-1 items-center gap-2 min-w-0">
                      <span className="flex-1 text-xs font-semibold truncate" style={{ color: 'var(--text-1)' }}>
                        {state.selectedExam.name}
                      </span>
                      <button
                        onClick={() => { dispatch({ type: 'SET_EXAM', payload: null }); setTermsAccepted(false); setSkipAutoFind(true) }}
                        className="flex-shrink-0 text-[0.65rem] px-1.5 py-0.5 rounded"
                        style={{ color: 'var(--text-3)' }}
                      >✕</button>
                    </div>
                  ) : (
                    <ExamCodeEntry skipAutoFind={skipAutoFind} onFound={exam => dispatch({ type: 'SET_EXAM', payload: exam })} />
                  )
                )}
              </div>

              {/* Mode — segmented toggle (hidden for standalone users) */}
              {examMode !== 'standalone' && (
                <div className="flex items-center gap-3 px-4 py-2.5 border-b" style={{ borderColor: 'var(--config-border)' }}>
                  <span className="text-[0.68rem] font-bold uppercase tracking-widest flex-shrink-0" style={{ color: 'var(--text-3)' }}>Mode</span>
                  <div className="flex flex-1 rounded-lg overflow-hidden" style={{ background: 'var(--chip-bg)', border: '1px solid var(--chip-border)' }}>
                    {[
                      ...(state.selectedExam?.trialsEnabled === false ? [] : [{ key: 'practice', icon: '📖', label: STRINGS.modes.practice }]),
                      { key: 'virtual',  icon: '⏱',  label: STRINGS.modes.virtual  },
                    ].map(({ key, icon, label }) => {
                      const sel    = state.mode === key
                      const locked = key === 'virtual' && examLockedByTrials
                      return (
                        <button key={key} disabled={locked}
                          title={locked ? 'Complete all Trial difficulty levels to unlock the exam' : undefined}
                          onClick={() => { if (locked) return; dispatch({ type: 'SET_MODE', payload: key }); setTermsAccepted(false) }}
                          className="flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-semibold transition-all"
                          style={sel
                            ? { background: 'var(--sel-bg)', color: 'var(--sel-text)', boxShadow: `0 0 12px var(--sel-shadow)` }
                            : { color: 'var(--text-2)', opacity: locked ? 0.5 : 1, cursor: locked ? 'not-allowed' : 'pointer' }}
                        >
                          <span>{locked ? '🔒' : icon}</span>
                          <span>{label}</span>
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* Difficulty — Trials only */}
              {!isVirtual && (
                <div className="p-4 border-b" style={{ borderColor: 'var(--config-border)' }}>
                  <p className="text-[0.68rem] font-bold uppercase tracking-widest mb-2" style={{ color: 'var(--text-3)' }}>Difficulty</p>
                  <div className="grid grid-cols-4 gap-1.5">
                    {DIFFICULTIES.map(({ key, icon, color, label }) => {
                      const sel    = activeDifficulty === key
                      const locked = isLevelLocked(key)
                      return (
                        <button key={key}
                          onClick={() => { if (!locked) dispatch({ type: 'SET_DIFFICULTY', payload: key }) }}
                          disabled={locked}
                          title={locked ? `Pass ${DIFFICULTIES[DIFFICULTIES.findIndex(d => d.key === key) - 1]?.label} first` : undefined}
                          className="p-2 rounded-xl text-center transition-all relative"
                          style={sel
                            ? { background: `${color}18`, border: `1px solid ${color}50`, boxShadow: `0 0 14px ${color}20` }
                            : { background: 'var(--chip-bg)', border: '1px solid var(--chip-border)', opacity: locked ? 0.5 : 1, cursor: locked ? 'not-allowed' : 'pointer' }}
                        >
                          {locked && <span className="absolute top-1 right-1 text-[0.6rem]">🔒</span>}
                          <span className="block text-lg mb-0.5">{icon}</span>
                          <span className="block text-[0.65rem] font-semibold truncate w-full" style={{ color: sel ? color : 'var(--text-2)' }}>{label}</span>
                          {!!trialProgress?.[key]?.attemptCount && (
                            <span className="block text-[0.58rem] mt-0.5" style={{ color: 'var(--text-3)' }}>
                              {trialProgress[key].attemptCount} attempt{trialProgress[key].attemptCount === 1 ? '' : 's'}
                            </span>
                          )}
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* Question breakdown — Trials only */}
              {!isVirtual && (
                <div className="p-4 border-b" style={{ borderColor: 'var(--config-border)' }}>
                  <p className="text-[0.68rem] font-bold uppercase tracking-widest mb-2" style={{ color: 'var(--text-3)' }}>Question Breakdown</p>
                  <div className="space-y-1">
                    {Q_TYPES.map(({ key, icon, label, min }) => {
                      const avail = availableCounts?.[key]
                      const maxVal = avail ?? 0
                      const disabled = !!availableCounts && maxVal === 0
                      return (
                        <div key={key} className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-xl"
                             style={{ background: 'var(--chip-bg)', border: '1px solid var(--chip-border)', opacity: disabled ? 0.4 : 1 }}>
                          <span className="text-sm flex-shrink-0">{icon}</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold" style={{ color: 'var(--text-1)' }}>{label}</p>
                            {availableCounts && (
                              <p className="text-[0.6rem]" style={{ color: 'var(--text-3)' }}>{maxVal} available</p>
                            )}
                          </div>
                          <Stepper value={qc[key] ?? 0} onChange={v => setCount(key, v)} min={Math.max(min, trialCountFloor(key))} max={availableCounts ? maxVal : 100} disabled={disabled} />
                        </div>
                      )
                    })}
                  </div>
                  {triedStartEmpty && total === 0 && (
                    <p className="text-xs mt-2 text-center" style={{ color: 'var(--red)' }}>
                      {availableCounts ? 'Select at least 1 question to start' : 'Add at least 1 question to start'}
                    </p>
                  )}
                </div>
              )}

              {/* Terms & Conditions — Exams mode only */}
              {isVirtual && (
                <div className="p-4 border-b" style={{ borderColor: 'var(--config-border)' }}>
                  {/* Header */}
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 text-sm"
                         style={{ background: 'rgba(251,191,36,0.12)', border: '1px solid rgba(251,191,36,0.25)' }}>
                      🔒
                    </div>
                    <p className="text-[0.68rem] font-bold uppercase tracking-widest flex-1" style={{ color: 'var(--text-3)' }}>
                      Exam Terms & Conditions
                    </p>
                    <button type="button" onClick={() => setTermsFullScreen(true)}
                      title="View full screen"
                      className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 transition-all"
                      style={{ background: 'var(--chip-bg)', border: '1px solid var(--chip-border)', color: 'var(--text-3)' }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--brand)'; e.currentTarget.style.color = 'var(--brand)' }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--chip-border)'; e.currentTarget.style.color = 'var(--text-3)' }}
                    >
                      <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M6 2H2v4M10 2h4v4M14 10v4h-4M2 10v4h4"/>
                      </svg>
                    </button>
                  </div>

                  {/* Rules */}
                  <div className="rounded-xl p-3 mb-3 space-y-2 overflow-y-auto"
                       style={{ background: 'rgba(251,191,36,0.04)', border: '1px solid rgba(251,191,36,0.12)', maxHeight: '180px' }}>
                    {EXAM_TERMS.map(({ icon, text }, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <span className="text-xs flex-shrink-0 mt-px">{icon}</span>
                        <span className="text-[0.7rem] leading-relaxed" style={{ color: 'var(--text-3)' }}>{text}</span>
                      </div>
                    ))}
                    {questionTypeNotes.length > 0 && (
                      <div className="pt-2 mt-2 border-t" style={{ borderColor: 'rgba(251,191,36,0.15)' }}>
                        <p className="text-[0.6rem] font-bold uppercase tracking-widest mb-1.5" style={{ color: 'var(--text-3)' }}>Question Type Grading</p>
                        {questionTypeNotes.map(({ icon, text }, i) => (
                          <div key={i} className="flex items-start gap-2 mt-1.5">
                            <span className="text-xs flex-shrink-0 mt-px">{icon}</span>
                            <span className="text-[0.7rem] leading-relaxed" style={{ color: 'var(--text-3)' }}>{text}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Agree checkbox */}
                  <label className="flex items-center gap-2.5 cursor-pointer select-none px-3 py-2.5 rounded-xl transition-all"
                         style={{
                           background: termsAccepted ? 'rgba(14,165,233,0.06)' : 'var(--chip-bg)',
                           border: `1px solid ${termsAccepted ? 'rgba(14,165,233,0.25)' : 'var(--chip-border)'}`,
                         }}>
                    <input type="checkbox" checked={termsAccepted} onChange={e => setTermsAccepted(e.target.checked)}
                           className="w-3.5 h-3.5 cursor-pointer accent-[var(--brand)] flex-shrink-0" />
                    <span className="text-xs font-semibold transition-colors"
                          style={{ color: termsAccepted ? 'var(--brand)' : 'var(--text-2)' }}>
                      {STRINGS.setup.termsCheckbox}
                    </span>
                    {termsAccepted && <span className="ml-auto text-xs" style={{ color: 'var(--brand)' }}>✓</span>}
                  </label>
                </div>
              )}

              {termsFullScreen && (
                <TermsFullScreenModal
                  questionTypeNotes={questionTypeNotes}
                  onClose={() => setTermsFullScreen(false)}
                />
              )}

              {/* Start */}
              <div className="p-4 space-y-2">
                <button
                  onClick={() => { if (!state.selectedExam) return; if (isVirtual && examLockedByTrials) return; if (isVirtual && !termsAccepted) return; if (!isVirtual && total === 0) { setTriedStartEmpty(true); return }; doStart() }}
                  disabled={starting || !state.selectedExam || (isVirtual && (examLockedByTrials || !termsAccepted))}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm transition-all disabled:opacity-50"
                  style={{
                    background: (starting || total === 0) ? 'var(--sel-bg)' : 'linear-gradient(135deg,var(--brand),#6366f1)',
                    color: '#fff',
                    boxShadow: (starting || total === 0) ? 'none' : '0 0 16px rgba(14,165,233,0.15)',
                  }}
                >
                  {starting
                    ? <><span className="w-4 h-4 rounded-full border-2 border-white/20 border-t-white animate-spin" /> Building exam…</>
                    : <>{isVirtual ? STRINGS.setup.startVirtual : `${STRINGS.setup.startPractice} · ${total} Qs`} →</>}
                </button>
                {activeSessionError && (
                  <p className="text-xs text-center px-1" style={{ color: 'var(--red)' }}>{activeSessionError}</p>
                )}
              </div>
            </div>
          </div>
        </div>

      </section>

      {buyCreditsOpen && (
        <BuyCreditsModal user={user} updateUser={updateUser} showToast={msg => setActiveSessionError(msg)} onClose={() => setBuyCreditsOpen(false)} />
      )}

      <style>{`
        @keyframes drift1 {
          0%,100% { transform: translate(0,0) scale(1); }
          33%      { transform: translate(50px,-40px) scale(1.08); }
          66%      { transform: translate(-25px,25px) scale(0.95); }
        }
        @keyframes drift2 {
          0%,100% { transform: translate(0,0) scale(1); }
          33%      { transform: translate(-40px,30px) scale(1.1); }
          66%      { transform: translate(30px,-20px) scale(0.92); }
        }
      `}</style>
    </>
  )
}
